"use client";

import { useSearchParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useDealership } from "@/contexts/DealershipContext";
import { PrintFilter } from "@/components/PrintHeader";

interface UsePrintFiltersOptions {
    /** Use group_name instead of participant_name as the identity chip. */
    isGroup?: boolean;
    /** Append the current department + positions selection (Comp/Turnover/Schedule). */
    includeDepartmentAndPositions?: boolean;
    /** Extra filters to append after the standard set. */
    extra?: PrintFilter[];
}

/**
 * Build the filter chips shown in the print-only PageTitle header.
 *
 * Pages that only differ by which "identity" they print (dealership vs. group)
 * and whether the dept/position selection is meaningful for the report just
 * pass options here instead of recomputing the same filter array each time.
 */
export function usePrintFilters(options: UsePrintFiltersOptions = {}): PrintFilter[] {
    const { t } = useTranslation();
    const {
        participantInfo,
        groupInfo,
        jobTitlesData,
        selectedPeerGroup,
        selectedDepartment,
        selectedPositionsIds,
    } = useDealership();
    const searchParams = useSearchParams();

    const effectivePeerGroup =
        selectedPeerGroup ||
        searchParams.get("peer_group") ||
        Object.entries(participantInfo?.peer_groups || {}).find(
            ([, info]) => info.peer_group_default
        )?.[0] ||
        "";

    const filters: PrintFilter[] = [];

    if (options.isGroup) {
        if (groupInfo?.group_name) {
            filters.push({ label: t("common.entities.group"), value: groupInfo.group_name });
        }
    } else if (participantInfo?.participant_name) {
        filters.push({
            label: t("common.entities.dealership"),
            value: participantInfo.participant_name,
        });
    }

    if (effectivePeerGroup) {
        filters.push({ label: t("common.entities.peerGroup"), value: effectivePeerGroup });
    }

    if (options.includeDepartmentAndPositions) {
        filters.push({
            label: t("common.entities.department"),
            value: t(`jobTitles.${selectedDepartment}`, selectedDepartment),
        });

        const departmentPositions = jobTitlesData?.[selectedDepartment] ?? {};
        const positionNames = selectedPositionsIds
            .map((id) => {
                if (id === -1) return t("common.entities.allPositions");
                const name = Object.keys(departmentPositions).find(
                    (n) => departmentPositions[n] === id
                );
                return name ? t(`jobTitles.${name}`, name) : null;
            })
            .filter(Boolean) as string[];

        if (positionNames.length > 0) {
            filters.push({
                label: t("common.entities.positions"),
                value: positionNames.join(", "),
            });
        }
    }

    if (options.extra && options.extra.length > 0) {
        filters.push(...options.extra);
    }

    return filters;
}
